import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import {
  Search,
  Phone,
  User,
  MapPin,
  Calendar,
  DollarSign,
  ShoppingBag,
  TrendingUp,
  Star,
  X,
  Heart,
  Clock,
  Mail,
  RefreshCw,
  MessageCircle,
  Send,
  Copy,
  Check,
  Users,
  Gift,
  Trophy,
  History,
  Target,
  AlertCircle,
  CheckCircle,
  XCircle,
  Save
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';

// Follow-up status configuration (same as DeliveryCRM)
const FOLLOW_UP_STATUSES = [
  { value: 'sin_contactar', label: 'Sin Contactar', color: 'bg-gray-500 text-white', icon: AlertCircle },
  { value: 'contactado', label: 'Contactado', color: 'bg-blue-500 text-white', icon: Phone },
  { value: 'interesado', label: 'Interesado', color: 'bg-yellow-500 text-white', icon: Star },
  { value: 'visitó', label: 'Visitó', color: 'bg-green-500 text-white', icon: CheckCircle },
  { value: 'no_interesado', label: 'No Interesado', color: 'bg-red-500 text-white', icon: XCircle }
];

// WhatsApp Message Templates
const MESSAGE_TEMPLATES = {
  points_reminder: {
    name: '📊 Recordatorio de Puntos',
    template: `🐸 *¡Hola {nombre}!*

Soy *Froggy* de *Mr. Frogs* 🍽️

📊 *Tu saldo actual:*
⭐ *{puntos} puntos* disponibles
💰 Valor: *${'{valor}'} MXN*

🎁 ¡Canjéalos por *premios GRATIS*!

👉 *Consulta tu tarjeta VIP aquí:*
{link}

¡Te esperamos pronto! 🐸✨

_Mr. Frogs - El mejor sabor del mar_`
  },
  vip_welcome: {
    name: '🏆 Bienvenida VIP',
    template: `🐸 *¡Hola {nombre}!*

¡Felicidades! 🎉 Eres parte de nuestro *Club VIP Mr. Frogs*

🏆 *Tu nivel:* {nivel}
⭐ *Puntos:* {puntos}
🛒 *Visitas:* {visitas}

✨ *Beneficios exclusivos:*
• Acumula puntos con cada compra
• Canjea por premios gratis
• Promociones especiales

👉 *Accede a tu tarjeta VIP:*
{link}

¡Gracias por tu preferencia! 🐸💚

_Mr. Frogs - El mejor sabor del mar_`
  },
  promo_special: {
    name: '🔥 Promoción Especial',
    template: `🐸 *¡{nombre}!*

🔥 *PROMOCIÓN ESPECIAL* solo para clientes VIP

Con tus *{puntos} puntos* puedes canjear increíbles premios 🎁

⭐ ¡No dejes que se acumulen, úsalos!

👉 *Canjea aquí:*
{link}

¡Te esperamos! 🍽️

_Mr. Frogs - El mejor sabor del mar_`
  },
  new_customer: {
    name: '👋 Nuevo Cliente',
    template: `🐸 *¡Hola {nombre}!*

¡Bienvenido a la familia *Mr. Frogs*! 🎉

Ya eres parte de nuestro *Club VIP* donde:
✅ Acumulas puntos con cada compra
✅ Canjeas por premios gratis
✅ Recibes promociones exclusivas

👉 *Consulta tus puntos aquí:*
{link}

¡Nos vemos pronto! 🐸💚

_Mr. Frogs - El mejor sabor del mar_`
  }
};

const Customers = () => {
  const { api } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // WhatsApp Modal State
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('points_reminder');
  const [whatsAppCustomers, setWhatsAppCustomers] = useState([]);
  const [filterType, setFilterType] = useState('all'); // all, with_points, vip, contacted, not_contacted
  const [copiedId, setCopiedId] = useState(null);
  const [sendingToCustomer, setSendingToCustomer] = useState(null);
  const [whatsAppSearch, setWhatsAppSearch] = useState(''); // Search in WhatsApp Masivo
  
  // Communication Stats State
  const [commStats, setCommStats] = useState(null);
  const [contactStatus, setContactStatus] = useState({});
  
  // Follow-up / Communication Tracking State (NEW)
  const [customerComms, setCustomerComms] = useState([]);
  const [loadingComms, setLoadingComms] = useState(false);
  const [editingFollowUp, setEditingFollowUp] = useState(false);
  const [followUpStatus, setFollowUpStatus] = useState('sin_contactar');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('info'); // info, communications

  // Get app link for customer portal
  const getAppLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/mi-tarjeta`;
  };

  // Generate personalized message for a customer
  const generateMessage = (customer, templateKey) => {
    const template = MESSAGE_TEMPLATES[templateKey].template;
    const points = customer.points_available || customer.points || 0;
    const valor = (points * 0.10).toFixed(2);
    const nivel = customer.vip_level || 'Bronce';
    const visitas = customer.visit_count || 0;
    
    return template
      .replace(/{nombre}/g, customer.name || 'Cliente')
      .replace(/{puntos}/g, points.toLocaleString())
      .replace(/{valor}/g, valor)
      .replace(/{nivel}/g, nivel)
      .replace(/{visitas}/g, visitas)
      .replace(/{link}/g, getAppLink());
  };

  // Copy message to clipboard
  const copyMessage = async (customer) => {
    const message = generateMessage(customer, selectedTemplate);
    try {
      await navigator.clipboard.writeText(message);
      setCopiedId(customer.customer_id);
      toast.success('Mensaje copiado');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Error al copiar');
    }
  };

  // Open WhatsApp with message
  const openWhatsApp = async (customer) => {
    const message = generateMessage(customer, selectedTemplate);
    const phone = customer.phone?.replace(/\D/g, '');
    
    if (!phone) {
      toast.error('Cliente sin teléfono');
      return;
    }
    
    // Add Mexico country code if not present
    const fullPhone = phone.startsWith('52') ? phone : `52${phone}`;
    
    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${fullPhone}?text=${encodedMessage}`;
    
    // Register communication in CRM
    setSendingToCustomer(customer.customer_id);
    try {
      // Map template to message type
      const messageTypeMap = {
        'points_reminder': 'reminder',
        'vip_welcome': 'welcome',
        'promo_special': 'promo',
        'birthday': 'birthday',
        'new_client': 'vip_card'
      };
      
      await api.post('/communications', {
        customer_id: customer.customer_id,
        customer_name: customer.name,
        customer_phone: phone,
        message_type: messageTypeMap[selectedTemplate] || 'custom',
        channel: 'whatsapp',
        message_content: MESSAGE_TEMPLATES[selectedTemplate]?.name || selectedTemplate
      });
      
      // Open WhatsApp
      window.open(whatsappUrl, '_blank');
      toast.success(`✅ Mensaje enviado a ${customer.name} (registrado en CRM)`);
    } catch (error) {
      // Still open WhatsApp even if CRM update fails
      window.open(whatsappUrl, '_blank');
      toast.warning('WhatsApp abierto (sin registro en CRM)');
      console.error('Error logging communication:', error);
    } finally {
      setSendingToCustomer(null);
    }
  };

  // Filter customers for WhatsApp
  const getFilteredWhatsAppCustomers = () => {
    let filtered = customers.filter(c => c.phone); // Must have phone
    
    switch (filterType) {
      case 'with_points':
        filtered = filtered.filter(c => (c.points_available || c.points || 0) > 0);
        break;
      case 'vip':
        filtered = filtered.filter(c => (c.total_spent || 0) >= 2000 || (c.visit_count || 0) >= 10);
        break;
      case 'new':
        filtered = filtered.filter(c => (c.visit_count || 0) <= 2);
        break;
      default:
        break;
    }
    
    return filtered;
  };

  // Open WhatsApp modal
  const openWhatsAppModal = () => {
    setWhatsAppCustomers(getFilteredWhatsAppCustomers());
    setShowWhatsAppModal(true);
  };

  useEffect(() => {
    fetchCustomers();
    fetchCommStats();
    fetchContactStatus();
  }, []);

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(res.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommStats = async () => {
    try {
      const res = await api.get('/communications/stats?days=30');
      setCommStats(res.data);
    } catch (error) {
      console.error('Error loading comm stats:', error);
    }
  };

  const fetchContactStatus = async () => {
    try {
      const res = await api.get('/communications/customers/contact-status?days=30');
      // Create a map for quick lookup
      const statusMap = {};
      for (const c of res.data.customers || []) {
        statusMap[c.customer_id] = {
          contacted: c.contacted_in_period,
          vipCardSent: c.vip_card_sent,
          lastContact: c.last_contact,
          contactCount: c.contact_count
        };
      }
      setContactStatus(statusMap);
    } catch (error) {
      console.error('Error loading contact status:', error);
    }
  };

  // Fetch communication history for a specific customer
  const fetchCustomerComms = async (customerId) => {
    setLoadingComms(true);
    try {
      const res = await api.get(`/communications/customer/${customerId}?limit=50`);
      setCustomerComms(res.data.timeline || []);
      // Note: followUpStatus is set from customer.follow_up_status in fetchCustomerDetail
      // We don't override it here anymore to preserve the customer's saved status
    } catch (error) {
      console.error('Error loading customer communications:', error);
      setCustomerComms([]);
    } finally {
      setLoadingComms(false);
    }
  };

  // Update follow-up status for a communication
  const handleSaveFollowUp = async () => {
    if (!selectedCustomer) return;
    
    setSavingFollowUp(true);
    try {
      // Log a new communication with the updated status
      await api.post('/communications', {
        customer_id: selectedCustomer.customer_id,
        customer_name: selectedCustomer.name,
        customer_phone: selectedCustomer.phone,
        message_type: 'custom',
        channel: 'seguimiento',
        message_content: `Estado: ${FOLLOW_UP_STATUSES.find(s => s.value === followUpStatus)?.label || followUpStatus}${followUpNotes ? ` - ${followUpNotes}` : ''}`
      });
      
      // Update the customer's follow_up_status directly
      await api.put(`/customers/${selectedCustomer.customer_id}`, {
        follow_up_status: followUpStatus
      });
      
      // Update local state
      setSelectedCustomer(prev => ({
        ...prev,
        follow_up_status: followUpStatus
      }));
      
      toast.success('Seguimiento actualizado');
      setEditingFollowUp(false);
      
      // Refresh communications and customer list
      fetchCustomerComms(selectedCustomer.customer_id);
      fetchContactStatus();
      fetchCustomers(); // Refresh the customer list to show updated status
    } catch (error) {
      console.error('Error saving follow-up:', error);
      toast.error('Error al guardar seguimiento');
    } finally {
      setSavingFollowUp(false);
    }
  };

  // Send quick WhatsApp from detail panel and log it
  const sendQuickWhatsApp = async (customer, messageType = 'custom') => {
    const phone = customer.phone?.replace(/\D/g, '');
    if (!phone) {
      toast.error('Cliente sin teléfono');
      return;
    }
    
    const fullPhone = phone.startsWith('52') ? phone : `52${phone}`;
    const message = generateMessage(customer, selectedTemplate);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${fullPhone}?text=${encodedMessage}`;
    
    try {
      await api.post('/communications', {
        customer_id: customer.customer_id,
        customer_name: customer.name,
        customer_phone: phone,
        message_type: messageType,
        channel: 'whatsapp',
        message_content: MESSAGE_TEMPLATES[selectedTemplate]?.name || 'Mensaje personalizado'
      });
      
      window.open(whatsappUrl, '_blank');
      toast.success('Mensaje enviado y registrado en CRM');
      
      // Refresh communications
      if (selectedCustomer?.customer_id === customer.customer_id) {
        fetchCustomerComms(customer.customer_id);
      }
      fetchContactStatus();
    } catch (error) {
      window.open(whatsappUrl, '_blank');
      toast.warning('WhatsApp abierto (sin registro en CRM)');
    }
  };

  const fetchCustomerDetail = async (customerId) => {
    setLoadingDetail(true);
    setActiveDetailTab('info'); // Reset to info tab
    try {
      const res = await api.get(`/customers/${customerId}`);
      setCustomerDetail(res.data);
      // Set the follow-up status from customer data (source of truth)
      setFollowUpStatus(res.data.follow_up_status || 'sin_contactar');
      setFollowUpNotes('');
      // Also fetch communications for this customer
      fetchCustomerComms(customerId);
    } catch (error) {
      toast.error('Error al cargar detalle del cliente');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSelectCustomer = (customer) => {
    if (selectedCustomer?.customer_id === customer.customer_id) {
      setSelectedCustomer(null);
      setCustomerDetail(null);
      setCustomerComms([]);
    } else {
      setSelectedCustomer(customer);
      fetchCustomerDetail(customer.customer_id);
    }
  };

  const filteredCustomers = customers.filter(c => {
    const query = searchQuery.toLowerCase();
    return (
      c.name?.toLowerCase().includes(query) ||
      c.phone?.includes(query) ||
      c.email?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nunca';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      timeZone: 'America/Hermosillo',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getCustomerSegment = (customer) => {
    const spent = customer.total_spent || 0;
    const visits = customer.visit_count || 0;
    
    if (spent >= 5000 || visits >= 20) return { label: 'VIP', color: 'bg-purple-500', textColor: 'text-purple-400' };
    if (spent >= 2000 || visits >= 10) return { label: 'Frecuente', color: 'bg-blue-500', textColor: 'text-blue-400' };
    if (spent >= 500 || visits >= 3) return { label: 'Regular', color: 'bg-green-500', textColor: 'text-green-400' };
    return { label: 'Nuevo', color: 'bg-gray-400', textColor: 'text-gray-400' };
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-3 md:p-8 pt-16 md:pt-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent/10">
                <User className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Clientes</h1>
                <p className="text-muted-foreground text-sm">{customers.length} clientes registrados</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={openWhatsAppModal}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp Masivo
              </Button>
              <Button variant="outline" size="icon" onClick={fetchCustomers}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Customer Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card 
              className={`cursor-pointer transition-all hover:shadow-lg ${filterType === 'all' ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => setFilterType('all')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{customers.length}</p>
                    <p className="text-xs text-muted-foreground">Total Clientes</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all hover:shadow-lg ${filterType === 'with_points' ? 'ring-2 ring-amber-500' : ''}`}
              onClick={() => setFilterType('with_points')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100">
                    <Star className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{customers.filter(c => (c.points_available || c.points || 0) > 0).length}</p>
                    <p className="text-xs text-muted-foreground">Con Puntos</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all hover:shadow-lg ${filterType === 'vip' ? 'ring-2 ring-purple-500' : ''}`}
              onClick={() => setFilterType('vip')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <Trophy className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{customers.filter(c => (c.total_spent || 0) >= 2000 || (c.visit_count || 0) >= 10).length}</p>
                    <p className="text-xs text-muted-foreground">VIP / Frecuentes</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card 
              className={`cursor-pointer transition-all hover:shadow-lg ${filterType === 'inactive' ? 'ring-2 ring-red-500' : ''}`}
              onClick={() => setFilterType('inactive')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-100">
                    <Clock className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{customers.filter(c => {
                      const lastVisit = c.last_visit ? new Date(c.last_visit) : null;
                      if (!lastVisit) return true;
                      const daysSinceVisit = Math.floor((new Date() - lastVisit) / (1000 * 60 * 60 * 24));
                      return daysSinceVisit > 30;
                    }).length}</p>
                    <p className="text-xs text-muted-foreground">Inactivos (+30 días)</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Communication Stats Panel */}
          {commStats && (
            <Card className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-green-800 flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    Seguimiento de Comunicaciones (Últimos 30 días)
                  </h3>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => { fetchCommStats(); fetchContactStatus(); }}
                    className="border-green-300"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <p className="text-2xl font-bold text-green-700">{commStats.total_sent}</p>
                    <p className="text-xs text-gray-500">Mensajes Enviados</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <p className="text-2xl font-bold text-blue-700">{commStats.unique_customers}</p>
                    <p className="text-xs text-gray-500">Clientes Contactados</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <p className="text-2xl font-bold text-amber-700">{commStats.by_status?.responded || 0}</p>
                    <p className="text-xs text-gray-500">Respondieron</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <p className="text-2xl font-bold text-purple-700">{commStats.by_status?.visited || 0}</p>
                    <p className="text-xs text-gray-500">Visitaron</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <p className="text-2xl font-bold text-green-600">{commStats.conversion_rate}%</p>
                    <p className="text-xs text-gray-500">Tasa Conversión</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Main Content - Flexbox Layout */}
          <div className="flex gap-6">
            {/* Left: Stats + Customer List */}
            <div className={`space-y-6 transition-all duration-300 ${selectedCustomer ? 'w-1/2' : 'w-full'}`}>
              
              {/* Stats Summary */}
              <div className={`grid ${selectedCustomer ? 'grid-cols-2' : 'grid-cols-4'} gap-4`}>
                <Card className="border border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Star className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{customers.filter(c => (c.total_spent || 0) >= 5000).length}</p>
                        <p className="text-xs text-muted-foreground">VIP</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{formatCurrency(customers.reduce((sum, c) => sum + (c.total_spent || 0), 0))}</p>
                        <p className="text-xs text-muted-foreground">Total Ventas</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {!selectedCustomer && (
                  <>
                    <Card className="border border-border/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Heart className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{customers.filter(c => (c.visit_count || 0) >= 10).length}</p>
                            <p className="text-xs text-muted-foreground">Frecuentes</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border border-border/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                            <ShoppingBag className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{customers.reduce((sum, c) => sum + (c.visit_count || 0), 0)}</p>
                            <p className="text-xs text-muted-foreground">Total Visitas</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border border-border/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <Star className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-2xl font-bold">{customers.reduce((sum, c) => sum + (c.points_available || 0), 0).toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">Puntos en Circulación</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Buscar por nombre, teléfono o email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-11"
                  data-testid="customer-search"
                />
              </div>

              {/* Customer List */}
              <Card className="border border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Lista de Clientes
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y max-h-[calc(100vh-400px)] overflow-y-auto">
                    {filteredCustomers.map(customer => {
                      const segment = getCustomerSegment(customer);
                      const isSelected = selectedCustomer?.customer_id === customer.customer_id;
                      const contactInfo = contactStatus[customer.customer_id] || {};
                      
                      return (
                        <div
                          key={customer.customer_id}
                          onClick={() => handleSelectCustomer(customer)}
                          className={`p-4 cursor-pointer transition-all hover:bg-gray-50 ${
                            isSelected ? 'bg-accent/10 border-l-4 border-l-accent' : ''
                          }`}
                          data-testid={`customer-card-${customer.customer_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center relative ${
                                isSelected ? 'bg-accent text-white' : 'bg-gray-100'
                              }`}>
                                <User className="w-5 h-5" />
                                {/* Contact status indicator */}
                                {contactInfo.contacted && (
                                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                                    <Check className="w-3 h-3 text-white" />
                                  </div>
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{customer.name}</p>
                                  <span className={`px-2 py-0.5 rounded-full text-xs text-white ${segment.color}`}>
                                    {segment.label}
                                  </span>
                                  {contactInfo.vipCardSent && (
                                    <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
                                      🎁 VIP
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {customer.phone}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-600">{formatCurrency(customer.total_spent)}</p>
                              <p className="text-xs text-muted-foreground">
                                {customer.visit_count || 0} visitas
                              </p>
                              {customer.points_available > 0 && (
                                <p className="text-xs font-medium text-amber-600">
                                  ⭐ {customer.points_available} pts
                                </p>
                              )}
                            </div>
                          </div>
                          {!selectedCustomer && (
                            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Última: {formatDate(customer.last_visit)}
                              </span>
                              {contactInfo.contacted ? (
                                <span className="flex items-center gap-1 text-green-600">
                                  <MessageCircle className="w-3 h-3" />
                                  Contactado ({contactInfo.contactCount}x)
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-orange-500">
                                  <Mail className="w-3 h-3" />
                                  Sin contactar
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {filteredCustomers.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No se encontraron clientes</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right: Customer Detail Panel (FUDO style) */}
            {selectedCustomer && (
              <div className="w-1/2 bg-slate-800 rounded-xl text-white overflow-hidden flex flex-col max-h-[calc(100vh-180px)]">
                {/* Panel Header */}
                <div className="bg-slate-900 p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getCustomerSegment(selectedCustomer).color}`}>
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-lg">{selectedCustomer.name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${getCustomerSegment(selectedCustomer).color}`}>
                          {getCustomerSegment(selectedCustomer).label}
                        </span>
                      </div>
                      <p className="text-slate-400 text-sm flex items-center gap-2">
                        <Phone className="w-3 h-3" />
                        {selectedCustomer.phone}
                        {selectedCustomer.email && (
                          <>
                            <span>•</span>
                            <Mail className="w-3 h-3" />
                            {selectedCustomer.email}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={() => { setSelectedCustomer(null); setCustomerDetail(null); }}
                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {loadingDetail ? (
                  <div className="flex-1 flex items-center justify-center">
                    <div className="animate-spin w-8 h-8 border-4 border-accent border-t-transparent rounded-full" />
                  </div>
                ) : customerDetail ? (
                  <>
                    {/* Customer Stats */}
                    <div className="grid grid-cols-3 gap-3 p-4 bg-slate-900/50">
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-400">{formatCurrency(customerDetail.total_spent)}</p>
                        <p className="text-xs text-slate-400">Total Gastado</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-accent">{customerDetail.visit_count || 0}</p>
                        <p className="text-xs text-slate-400">Visitas</p>
                      </div>
                      <div className="bg-slate-700/50 rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-amber-400">
                          {selectedCustomer.points_available || 0}
                        </p>
                        <p className="text-xs text-slate-400">Puntos VIP</p>
                      </div>
                    </div>

                    {/* Loyalty Points Action */}
                    {selectedCustomer.points_available > 0 && (
                      <div className="mx-4 p-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Star className="w-5 h-5 text-amber-400" />
                            <div>
                              <p className="text-sm font-medium text-white">
                                {selectedCustomer.points_available} puntos = ${(selectedCustomer.points_available * 0.10).toFixed(2)}
                              </p>
                              <p className="text-xs text-slate-400">Disponibles para canjear</p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => sendQuickWhatsApp(selectedCustomer, 'vip_card')}
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            <Phone className="w-4 h-4 mr-1" />
                            WhatsApp
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Tab Navigation */}
                    <div className="flex border-b border-slate-700 mx-4 mt-2">
                      <button
                        onClick={() => setActiveDetailTab('info')}
                        className={`px-4 py-2 text-sm font-medium transition-colors ${
                          activeDetailTab === 'info' 
                            ? 'text-white border-b-2 border-accent' 
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <User className="w-4 h-4 inline mr-1" />
                        Info
                      </button>
                      <button
                        onClick={() => setActiveDetailTab('communications')}
                        className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                          activeDetailTab === 'communications' 
                            ? 'text-white border-b-2 border-green-500' 
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        <MessageCircle className="w-4 h-4" />
                        Seguimiento
                        {customerComms.length > 0 && (
                          <span className="bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                            {customerComms.length}
                          </span>
                        )}
                      </button>
                    </div>

                    {/* Content based on active tab */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      
                      {/* INFO TAB */}
                      {activeDetailTab === 'info' && (
                        <>
                          {/* Favorite Products */}
                          <div>
                            <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                              <Heart className="w-4 h-4" />
                              Productos Favoritos
                            </h4>
                            {customerDetail.favorite_products?.length > 0 ? (
                              <div className="space-y-2">
                                {customerDetail.favorite_products.slice(0, 5).map((prod, idx) => (
                                  <div key={idx} className="bg-slate-700/50 rounded-lg p-3 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <span className="w-6 h-6 bg-accent/20 text-accent rounded-full flex items-center justify-center text-sm font-bold">
                                        {idx + 1}
                                      </span>
                                      <span className="text-sm">{prod.name}</span>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-bold text-green-400">{formatCurrency(prod.total)}</p>
                                      <p className="text-xs text-slate-400">{prod.count}x</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-slate-500 text-center py-3 bg-slate-700/30 rounded-lg text-sm">Sin datos</p>
                            )}
                          </div>

                      {/* Order History */}
                      <div>
                        <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                          <ShoppingBag className="w-4 h-4" />
                          Últimos Pedidos
                        </h4>
                        {customerDetail.order_history?.length > 0 ? (
                          <div className="space-y-2">
                            {customerDetail.order_history.slice(0, 5).map((order, idx) => (
                              <div key={idx} className="bg-slate-700/50 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-accent font-bold">#{order.order_number}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs ${
                                      order.order_type === 'dine_in' ? 'bg-blue-500/20 text-blue-300' :
                                      order.order_type === 'takeout' ? 'bg-yellow-500/20 text-yellow-300' :
                                      'bg-purple-500/20 text-purple-300'
                                    }`}>
                                      {order.order_type === 'dine_in' ? 'Mesa' : 
                                       order.order_type === 'takeout' ? 'P/Llevar' : 'Domicilio'}
                                    </span>
                                  </div>
                                  <span className="font-bold text-green-400">{formatCurrency(order.total)}</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-slate-400">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {formatDate(order.created_at)}
                                  </span>
                                  <span>•</span>
                                  <span>
                                    {order.items?.slice(0, 2).map(i => i.product_name).join(', ')}
                                    {order.items?.length > 2 && ` +${order.items.length - 2}`}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-500 text-center py-3 bg-slate-700/30 rounded-lg text-sm">Sin pedidos</p>
                        )}
                      </div>

                      {/* Monthly Stats */}
                      {customerDetail.monthly_stats?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Consumo Mensual
                          </h4>
                          <div className="space-y-2">
                            {customerDetail.monthly_stats.slice(0, 3).map((stat, idx) => (
                              <div key={idx} className="bg-slate-700/50 rounded-lg p-3 flex items-center justify-between">
                                <span className="text-sm text-slate-300">{stat.month}</span>
                                <div className="flex items-center gap-4">
                                  <span className="text-xs text-slate-400">{stat.orders} pedidos</span>
                                  <span className="font-bold text-green-400">{formatCurrency(stat.total)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Marketing Insight */}
                      {customerDetail.favorite_products?.length > 0 && (
                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                          <p className="text-sm text-blue-300">
                            <strong>💡 Sugerencia:</strong> Este cliente prefiere{' '}
                            <strong>{customerDetail.favorite_products[0]?.name}</strong>. 
                            Considera ofrecer promociones en productos similares.
                          </p>
                        </div>
                      )}
                        </>
                      )}

                      {/* COMMUNICATIONS TAB */}
                      {activeDetailTab === 'communications' && (
                        <>
                          {/* Current Follow-up Status Card */}
                          <div className="bg-gradient-to-br from-slate-700/50 to-slate-800/50 rounded-xl p-4 border border-slate-600">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-medium text-slate-300 uppercase tracking-wide flex items-center gap-2">
                                <Target className="w-4 h-4 text-green-400" />
                                Estado de Seguimiento
                              </h4>
                              {!editingFollowUp && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => setEditingFollowUp(true)}
                                  className="text-accent hover:bg-slate-600"
                                >
                                  Cambiar
                                </Button>
                              )}
                            </div>
                            
                            {editingFollowUp ? (
                              <div className="space-y-3">
                                <div>
                                  <Label className="text-slate-300 text-xs">Estado</Label>
                                  <Select value={followUpStatus} onValueChange={setFollowUpStatus}>
                                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {FOLLOW_UP_STATUSES.map(status => (
                                        <SelectItem key={status.value} value={status.value}>
                                          <span className="flex items-center gap-2">
                                            <span className={`w-2 h-2 rounded-full ${status.color.replace('text-white', '')}`} />
                                            {status.label}
                                          </span>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-slate-300 text-xs">Notas</Label>
                                  <Input
                                    value={followUpNotes}
                                    onChange={(e) => setFollowUpNotes(e.target.value)}
                                    placeholder="Ej: Dijo que viene el viernes..."
                                    className="bg-slate-700 border-slate-600 text-white mt-1"
                                  />
                                </div>
                                <div className="flex gap-2">
                                  <Button 
                                    size="sm" 
                                    onClick={handleSaveFollowUp}
                                    disabled={savingFollowUp}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    {savingFollowUp ? (
                                      <RefreshCw className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Save className="w-4 h-4 mr-1" />
                                    )}
                                    Guardar
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => setEditingFollowUp(false)}
                                    className="text-slate-300"
                                  >
                                    Cancelar
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-3">
                                {(() => {
                                  const currentStatus = FOLLOW_UP_STATUSES.find(s => s.value === followUpStatus) || FOLLOW_UP_STATUSES[0];
                                  const StatusIcon = currentStatus.icon;
                                  return (
                                    <>
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStatus.color}`}>
                                        <StatusIcon className="w-5 h-5" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-white">{currentStatus.label}</p>
                                        {followUpNotes && (
                                          <p className="text-sm text-slate-400">{followUpNotes}</p>
                                        )}
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            )}
                          </div>

                          {/* Quick WhatsApp Action */}
                          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Phone className="w-5 h-5 text-green-400" />
                                <div>
                                  <p className="text-sm font-medium text-white">Enviar WhatsApp</p>
                                  <p className="text-xs text-slate-400">Se registrará automáticamente</p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => sendQuickWhatsApp(selectedCustomer, 'promo')}
                                className="bg-green-500 hover:bg-green-600"
                              >
                                <Send className="w-4 h-4 mr-1" />
                                Enviar
                              </Button>
                            </div>
                          </div>

                          {/* Communication Timeline */}
                          <div>
                            <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
                              <History className="w-4 h-4" />
                              Historial de Comunicaciones
                            </h4>
                            
                            {loadingComms ? (
                              <div className="flex items-center justify-center py-6">
                                <RefreshCw className="w-6 h-6 animate-spin text-accent" />
                              </div>
                            ) : customerComms.length > 0 ? (
                              <div className="space-y-3">
                                {customerComms.map((comm, idx) => (
                                  <div 
                                    key={comm.communication_id || idx}
                                    className="bg-slate-700/50 rounded-lg p-3 border-l-4 border-green-500"
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="flex items-center gap-2 text-sm font-medium text-white">
                                        {comm.channel === 'whatsapp' ? (
                                          <MessageCircle className="w-4 h-4 text-green-400" />
                                        ) : (
                                          <Target className="w-4 h-4 text-blue-400" />
                                        )}
                                        {comm.message_type === 'welcome' && 'Bienvenida'}
                                        {comm.message_type === 'promo' && 'Promoción'}
                                        {comm.message_type === 'vip_card' && 'Tarjeta VIP'}
                                        {comm.message_type === 'reminder' && 'Recordatorio'}
                                        {comm.message_type === 'custom' && 'Seguimiento'}
                                        {!['welcome', 'promo', 'vip_card', 'reminder', 'custom'].includes(comm.message_type) && comm.message_type}
                                      </span>
                                      <span className="text-xs text-slate-400">
                                        {comm.sent_at ? new Date(comm.sent_at).toLocaleDateString('es-MX', {
                                          day: 'numeric',
                                          month: 'short',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        }) : 'Sin fecha'}
                                      </span>
                                    </div>
                                    {comm.message_content && (
                                      <p className="text-sm text-slate-300 mt-1">{comm.message_content}</p>
                                    )}
                                    {comm.status && comm.status !== 'sent' && (
                                      <div className="mt-2">
                                        {(() => {
                                          const statusInfo = FOLLOW_UP_STATUSES.find(s => s.value === comm.status);
                                          return statusInfo ? (
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusInfo.color}`}>
                                              {statusInfo.label}
                                            </span>
                                          ) : null;
                                        })()}
                                      </div>
                                    )}
                                    {comm.sent_by_name && (
                                      <p className="text-xs text-slate-500 mt-1">Por: {comm.sent_by_name}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-6 bg-slate-700/30 rounded-lg">
                                <MessageCircle className="w-10 h-10 mx-auto mb-2 text-slate-500" />
                                <p className="text-slate-400 text-sm">Sin comunicaciones registradas</p>
                                <p className="text-slate-500 text-xs mt-1">Envía un WhatsApp para iniciar el seguimiento</p>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* WhatsApp Mass Messaging Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">WhatsApp Masivo</h2>
                  <p className="text-green-100 text-sm">Envía mensajes personalizados a tus clientes</p>
                </div>
              </div>
              <button 
                onClick={() => setShowWhatsAppModal(false)}
                className="text-white/80 hover:text-white p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex h-[calc(90vh-80px)]">
              {/* Left Panel - Settings */}
              <div className="w-1/3 border-r bg-gray-50 p-4 space-y-4 overflow-y-auto">
                {/* Template Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📝 Plantilla de Mensaje
                  </label>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="w-full p-3 border rounded-lg bg-white focus:ring-2 focus:ring-green-500"
                  >
                    {Object.entries(MESSAGE_TEMPLATES).map(([key, tmpl]) => (
                      <option key={key} value={key}>{tmpl.name}</option>
                    ))}
                  </select>
                </div>

                {/* Filter Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    👥 Filtrar Clientes
                  </label>
                  <div className="space-y-2">
                    <button
                      onClick={() => { setFilterType('all'); setWhatsAppCustomers(customers.filter(c => c.phone)); }}
                      className={`w-full p-3 rounded-lg text-left flex items-center justify-between ${
                        filterType === 'all' ? 'bg-green-100 border-2 border-green-500' : 'bg-white border'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Todos
                      </span>
                      <span className="text-sm text-gray-500">{customers.filter(c => c.phone).length}</span>
                    </button>
                    <button
                      onClick={() => { setFilterType('with_points'); setWhatsAppCustomers(customers.filter(c => c.phone && (c.points_available || c.points || 0) > 0)); }}
                      className={`w-full p-3 rounded-lg text-left flex items-center justify-between ${
                        filterType === 'with_points' ? 'bg-green-100 border-2 border-green-500' : 'bg-white border'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-amber-500" />
                        Con Puntos
                      </span>
                      <span className="text-sm text-gray-500">{customers.filter(c => c.phone && (c.points_available || c.points || 0) > 0).length}</span>
                    </button>
                    <button
                      onClick={() => { setFilterType('vip'); setWhatsAppCustomers(customers.filter(c => c.phone && ((c.total_spent || 0) >= 2000 || (c.visit_count || 0) >= 10))); }}
                      className={`w-full p-3 rounded-lg text-left flex items-center justify-between ${
                        filterType === 'vip' ? 'bg-green-100 border-2 border-green-500' : 'bg-white border'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-purple-500" />
                        VIP / Frecuentes
                      </span>
                      <span className="text-sm text-gray-500">{customers.filter(c => c.phone && ((c.total_spent || 0) >= 2000 || (c.visit_count || 0) >= 10)).length}</span>
                    </button>
                    <button
                      onClick={() => { setFilterType('new'); setWhatsAppCustomers(customers.filter(c => c.phone && (c.visit_count || 0) <= 2)); }}
                      className={`w-full p-3 rounded-lg text-left flex items-center justify-between ${
                        filterType === 'new' ? 'bg-green-100 border-2 border-green-500' : 'bg-white border'
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <User className="w-4 h-4 text-blue-500" />
                        Nuevos
                      </span>
                      <span className="text-sm text-gray-500">{customers.filter(c => c.phone && (c.visit_count || 0) <= 2).length}</span>
                    </button>
                    
                    {/* Contact Status Filters */}
                    <div className="border-t pt-3 mt-3">
                      <p className="text-xs text-gray-500 mb-2 font-medium">SEGUIMIENTO</p>
                      <button
                        onClick={() => { 
                          setFilterType('not_contacted'); 
                          setWhatsAppCustomers(customers.filter(c => c.phone && !contactStatus[c.customer_id]?.contacted)); 
                        }}
                        className={`w-full p-3 rounded-lg text-left flex items-center justify-between ${
                          filterType === 'not_contacted' ? 'bg-orange-100 border-2 border-orange-500' : 'bg-white border'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-orange-500" />
                          Sin Contactar
                        </span>
                        <span className="text-sm text-orange-600 font-medium">
                          {customers.filter(c => c.phone && !contactStatus[c.customer_id]?.contacted).length}
                        </span>
                      </button>
                      <button
                        onClick={() => { 
                          setFilterType('contacted'); 
                          setWhatsAppCustomers(customers.filter(c => c.phone && contactStatus[c.customer_id]?.contacted)); 
                        }}
                        className={`w-full p-3 rounded-lg text-left flex items-center justify-between mt-2 ${
                          filterType === 'contacted' ? 'bg-green-100 border-2 border-green-500' : 'bg-white border'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" />
                          Ya Contactados
                        </span>
                        <span className="text-sm text-green-600 font-medium">
                          {customers.filter(c => c.phone && contactStatus[c.customer_id]?.contacted).length}
                        </span>
                      </button>
                      <button
                        onClick={() => { 
                          setFilterType('no_vip_card'); 
                          setWhatsAppCustomers(customers.filter(c => c.phone && !contactStatus[c.customer_id]?.vipCardSent)); 
                        }}
                        className={`w-full p-3 rounded-lg text-left flex items-center justify-between mt-2 ${
                          filterType === 'no_vip_card' ? 'bg-purple-100 border-2 border-purple-500' : 'bg-white border'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Gift className="w-4 h-4 text-purple-500" />
                          Sin Tarjeta VIP
                        </span>
                        <span className="text-sm text-purple-600 font-medium">
                          {customers.filter(c => c.phone && !contactStatus[c.customer_id]?.vipCardSent).length}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    👁️ Vista Previa del Mensaje
                  </label>
                  <div className="bg-[#e5ddd5] p-3 rounded-lg">
                    <div className="bg-white rounded-lg p-3 shadow-sm max-h-60 overflow-y-auto">
                      <pre className="text-xs whitespace-pre-wrap font-sans">
                        {generateMessage({ name: 'Juan', points_available: 500, vip_level: 'Plata', visit_count: 5 }, selectedTemplate)}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Panel - Customer List */}
              <div className="flex-1 flex flex-col">
                <div className="p-4 border-b bg-white space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium">
                      📱 {whatsAppCustomers.filter(c => {
                        if (!whatsAppSearch) return true;
                        const search = whatsAppSearch.toLowerCase();
                        return (c.name || '').toLowerCase().includes(search) || 
                               (c.phone || '').includes(search);
                      }).length} clientes seleccionados
                    </p>
                    <p className="text-sm text-gray-500">
                      Clic en 📋 para copiar o ▶️ para abrir WhatsApp
                    </p>
                  </div>
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar por nombre o teléfono..."
                      value={whatsAppSearch}
                      onChange={(e) => setWhatsAppSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      data-testid="whatsapp-masivo-search"
                    />
                    {whatsAppSearch && (
                      <button
                        onClick={() => setWhatsAppSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-2">
                    {whatsAppCustomers
                      .filter(c => {
                        if (!whatsAppSearch) return true;
                        const search = whatsAppSearch.toLowerCase();
                        return (c.name || '').toLowerCase().includes(search) || 
                               (c.phone || '').includes(search);
                      })
                      .map(customer => (
                      <div 
                        key={customer.customer_id}
                        className="bg-white border rounded-lg p-3 flex items-center justify-between hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium">{customer.name}</p>
                            <p className="text-sm text-gray-500 flex items-center gap-2">
                              <Phone className="w-3 h-3" />
                              {customer.phone}
                              {(customer.points_available || customer.points || 0) > 0 && (
                                <span className="text-amber-600 font-medium">
                                  ⭐ {(customer.points_available || customer.points || 0).toLocaleString()} pts
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyMessage(customer)}
                            className="h-9"
                          >
                            {copiedId === customer.customer_id ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openWhatsApp(customer)}
                            disabled={sendingToCustomer === customer.customer_id}
                            className="h-9 bg-green-500 hover:bg-green-600 text-white"
                          >
                            {sendingToCustomer === customer.customer_id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-1" />
                                Enviar
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}

                    {whatsAppCustomers
                      .filter(c => {
                        if (!whatsAppSearch) return true;
                        const search = whatsAppSearch.toLowerCase();
                        return (c.name || '').toLowerCase().includes(search) || 
                               (c.phone || '').includes(search);
                      }).length === 0 && (
                      <div className="text-center py-10 text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>{whatsAppSearch ? 'No se encontraron clientes con ese término' : 'No hay clientes con este filtro'}</p>
                        {whatsAppSearch && (
                          <button 
                            onClick={() => setWhatsAppSearch('')}
                            className="mt-2 text-green-600 hover:text-green-700 text-sm"
                          >
                            Limpiar búsqueda
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50">
                  <p className="text-xs text-gray-500 text-center">
                    💡 Al enviar, se registrará automáticamente en el seguimiento CRM del cliente
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
